import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { API } from "../config/apiConfig";
import { portalGradientTextSx } from "../styles/dashboardBrand";
import {
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  CircularProgress,
  Box,
  IconButton,
  InputAdornment,
} from "@mui/material";
import SecurityIcon from "@mui/icons-material/Security";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

function ResetPassword() {
  const location = useLocation();
  const navigate = useNavigate();
  const { email, verificationCode } = location.state || {};

  const [form, setForm] = useState({
    newPassword: "",
    confirmPassword: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Redirect if no email or verification code
  useEffect(() => {
    if (!email || !verificationCode) {
      navigate("/forgot-password");
    }
  }, [email, verificationCode, navigate]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!form.newPassword) {
      setError("Please enter a new password");
      return;
    }

    if (form.newPassword.length < 6) {
      setError("Password must be at least 6 characters long");
      return;
    }

    if (form.newPassword !== form.confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    try {
      setLoading(true);

      const payload = {
        email: email,
        verificationCode: verificationCode,
        newPassword: form.newPassword,
      };

      console.log("Reset password payload:", payload);

      const res = await axios.post(API.resetPassword, payload, {
        headers: { "Content-Type": "application/json" },
      });

      if (res.data.success) {
        setSuccess("Password reset successfully! Redirecting to login...");
        
        // Redirect to login after 2 seconds
        setTimeout(() => {
          navigate("/login");
        }, 2000);
      } else {
        setError(res.data.error || "Failed to reset password. Please try again.");
      }
    } catch (err) {
      console.error("❌ Reset password error:", err.response?.data || err.message);
      setError(
        err.response?.data?.error ||
          err.response?.data?.message ||
          err.message ||
          "❌ Network error. Please check your connection and try again."
      );
    } finally {
      setLoading(false);
    }
  };

  if (!email || !verificationCode) {
    return null; // Will redirect
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: 'linear-gradient(135deg, #4FACFE 0%, #6366F1 40%, #9333EA 72%, #E11D48 100%)',
        position: 'relative',
        padding: 2,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.3) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.3) 0%, transparent 50%), radial-gradient(circle at 40% 40%, rgba(120, 219, 255, 0.2) 0%, transparent 50%)',
          pointerEvents: 'none',
        },
      }}
    >
      <Paper 
        elevation={24} 
        sx={{ 
          p: 5, 
          width: "100%", 
          maxWidth: 450,
          borderRadius: 3,
          bgcolor: 'background.paper',
          backdropFilter: 'blur(20px)',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Stack spacing={3} alignItems="center">
          {/* Header Icon */}
          <SecurityIcon sx={{ fontSize: 60, color: "primary.main" }} />

          {/* Title */}
          <Typography variant="h4" sx={portalGradientTextSx({ fontWeight: 'bold', textAlign: 'center' })}>
            Reset Your Password
          </Typography>

          {/* Description */}
          <Typography variant="body1" color="text.secondary" textAlign="center">
            Enter your new password below.
          </Typography>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ width: "100%" }}>
              {error}
            </Alert>
          )}

          {/* Success Alert */}
          {success && (
            <Alert severity="success" sx={{ width: "100%" }}>
              {success}
            </Alert>
          )}

          {/* Form */}
          <Box component="form" onSubmit={handleSubmit} sx={{ width: "100%" }}>
            <Stack spacing={3}>
              {/* New Password Field */}
              <TextField
                name="newPassword"
                label="New Password"
                type={showPassword ? "text" : "password"}
                value={form.newPassword}
                onChange={handleChange}
                required
                fullWidth
                disabled={loading}
                placeholder="Enter your new password"
                autoComplete="new-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* Confirm Password Field */}
              <TextField
                name="confirmPassword"
                label="Confirm New Password"
                type={showConfirmPassword ? "text" : "password"}
                value={form.confirmPassword}
                onChange={handleChange}
                required
                fullWidth
                disabled={loading}
                placeholder="Confirm your new password"
                autoComplete="new-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                      >
                        {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* Submit Button */}
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={loading}
                fullWidth
                sx={{ py: 1.5 }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Reset Password"
                )}
              </Button>

              {/* Back to Login */}
              <Button
                variant="text"
                onClick={() => navigate("/login")}
                disabled={loading}
                sx={{ textTransform: "none" }}
              >
                Back to Login
              </Button>
            </Stack>
          </Box>

          {/* Help Text */}
          <Typography variant="body2" color="text.secondary" textAlign="center">
            Your password must be at least 6 characters long.
          </Typography>
        </Stack>
      </Paper>
    </Box>
  );
}

export default ResetPassword;